// ============================================
// APPLICATION - Auth Hooks
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { authRepository } from '../../infrastructure/http/auth.repository';
import { sessionRepository } from '../../infrastructure/http/session.repository';
import { useAuthStore } from '../../state/auth.store';
import { queryKeys } from '../query-keys';
import type { LoginDto, RegisterDto } from '../../domain/dtos';
import { handleApiError } from '../../infrastructure/http/axios-client';

/**
 * Hook para inicializar autenticación al cargar la app
 * Intenta restaurar sesión con refresh token (cookie HttpOnly)
 */
export const useInitAuth = () => {
  const queryClient = useQueryClient();
  const isInitialized = useAuthStore((state) => state.isInitialized);

  useEffect(() => {
    const initialize = async () => {
      const store = useAuthStore.getState();
      
      // Solo inicializar una vez
      if (store.isInitialized) {
        //console.log('[useInitAuth] Ya inicializado, skip refresh');
        return;
      }
      
      //console.log('[useInitAuth] Ejecutando inicialización (refresh token)...');
      
      try {
        const success = await store.initializeAuth();
        //console.log('[useInitAuth] Inicialización completa. Éxito:', success);
        
        // Si la restauración fue exitosa, invalidar para cargar sesión
        if (success) {
          //console.log('[useInitAuth] Invalidando query de sesión para forzar carga');
          queryClient.invalidateQueries({ queryKey: ['session'] });
        }
      } catch (error) {
        //console.error('[useInitAuth] Error en inicialización:', error);
      }
    };

    initialize();
  }, []); // Solo en el mount

  return { isInitialized };
};

/**
 * Hook para obtener perfil del usuario autenticado
 */
export const useProfile = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: queryKeys.auth.profile(),
    queryFn: authRepository.getProfile,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });
};

/**
 * Hook para login
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  const { setUsuario, setAccessToken } = useAuthStore();

  return useMutation({
    mutationFn: (dto: LoginDto) => authRepository.login(dto),
    onSuccess: (data) => {
      // Crear objeto de usuario compatible con el store
      const usuario = {
        id: String(data.usuarioId),
        nombre: data.nombre,
        apellidos: data.apellidos,
        almacenId: String(data.almacenId),
        permisos: data.permisos,
      };
      
      //console.log('[useLogin] Login exitoso, estableciendo tokens y usuario');
      
      // Actualizar store con token en memoria y usuario
      setAccessToken(data.token, data.expiresInSeconds);
      setUsuario(usuario as any);
      
      // Marcar como inicializado para evitar refresh en mount
      useAuthStore.setState({ isInitialized: true });

      // Invalidar query de session para que se ejecute automáticamente
      queryClient.invalidateQueries({ queryKey: ['session'] });
      // Defensivo: hoy la única forma de cambiar de sucursal en la UI es
      // pasando por useLogout (que ya hace queryClient.clear()), así que esto
      // nunca debería tener nada que invalidar en la práctica. Pero si el día
      // de mañana se agrega un atajo de "cambiar sucursal" sin logout previo,
      // esto evita que el turno/turnos-otras-sucursales de la sesión anterior
      // quede cacheado y se muestre bajo la sesión nueva.
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
      
      // Enviar heartbeat para registrar actividad (no bloquea el flujo)
      const now = Date.now();
      localStorage.setItem('almacenes_last_heartbeat', now.toString());
      sessionRepository.sendHeartbeat().catch((error) => {
        console.error('[useLogin] Error al enviar heartbeat:', error);
      });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para registro
 */
export const useRegister = () => {
  const queryClient = useQueryClient();
  const { setUsuario, setAccessToken } = useAuthStore();

  return useMutation({
    mutationFn: (dto: RegisterDto) => authRepository.register(dto),
    onSuccess: ({ usuario, tokens }) => {
      setUsuario(usuario);
      // Si el backend retorna tokens con accessToken y expiresInSeconds
      if ('expiresInSeconds' in tokens) {
        setAccessToken((tokens as any).accessToken, (tokens as any).expiresInSeconds);
      }
      queryClient.setQueryData(queryKeys.auth.profile(), usuario);
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para completar registro
 */
export const useCompleteSignup = () => {
  const queryClient = useQueryClient();
  const { setUsuario, setAccessToken } = useAuthStore();

  return useMutation({
    mutationFn: (dto: {
      usuarioId: number;
      almacenId: number;
      nombreComercial: string;
      direccion: string;
      numeroDireccion: number;
      local: string | null;
      comunaId: number;
      tienePatenteAlcohol: boolean;
      tieneFrios: boolean;
      tieneCongelados: boolean;
      vendePorMayor: boolean;
      tamanoLocal: string;
      tipoAlmacen: string;
      claveSii: string | null;
      clavePropietario: string;
      nombreVendedor: string;
      claveVendedor: string;
    }) => authRepository.completeSignup(dto),
    onSuccess: (data) => {
      // Crear objeto de usuario compatible con el store
      const usuario = {
        id: String(data.usuarioId),
        nombre: data.nombre,
        apellidos: data.apellidos,
        almacenId: String(data.almacenId),
        permisos: data.permisos,
      };
      
      //console.log('[useCompleteSignup] Registro completado, estableciendo tokens y usuario');
      
      // Actualizar store con token en memoria y usuario
      setAccessToken(data.token, data.expiresInSeconds);
      setUsuario(usuario as any);
      
      // Marcar como inicializado para evitar refresh en mount
      useAuthStore.setState({ isInitialized: true });

      // Invalidar query de session para que se ejecute automáticamente
      queryClient.invalidateQueries({ queryKey: ['session'] });
      // Defensivo: hoy la única forma de cambiar de sucursal en la UI es
      // pasando por useLogout (que ya hace queryClient.clear()), así que esto
      // nunca debería tener nada que invalidar en la práctica. Pero si el día
      // de mañana se agrega un atajo de "cambiar sucursal" sin logout previo,
      // esto evita que el turno/turnos-otras-sucursales de la sesión anterior
      // quede cacheado y se muestre bajo la sesión nueva.
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
      
      // Enviar heartbeat para registrar actividad (no bloquea el flujo)
      const now = Date.now();
      localStorage.setItem('almacenes_last_heartbeat', now.toString());
      sessionRepository.sendHeartbeat().catch((error) => {
        console.error('[useCompleteSignup] Error al enviar heartbeat:', error);
      });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para logout
 */
const navigateToLogin = () => {
  // En Electron (createHashRouter) la ruta vive en el hash; window.location.href
  // apuntaría a un archivo inexistente y dejaría la ventana en blanco.
  if ((window as any).electronAPI) {
    window.location.hash = '/auth/login';
  } else {
    window.location.href = '/auth/login';
  }
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const { logout } = useAuthStore();

  return useMutation({
    mutationFn: authRepository.logout,
    onSuccess: () => {
      logout();
      queryClient.clear();
      localStorage.removeItem('almacenes-digitales-cache');
      navigateToLogin();
    },
    onError: () => {
      logout();
      queryClient.clear();
      localStorage.removeItem('almacenes-digitales-cache');
      navigateToLogin();
    },
  });
};

/**
 * Hook para iniciar registro de almacén (envía correo de verificación)
 */
export const useSignupInitiate = () => {
  return useMutation({
    mutationFn: (dto: {
      rutAlmacen: string;
      runPersonal: string;
      nombrePropietario: string;
      apellidosPropietario: string;
      email: string;
      telefono: number;
      medioInformacion: string;
    }) => authRepository.signupInitiate(dto),
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para verificar estado de registro
 */
export const useCheckSignupStatus = (
  userId: string | null,
  almacenId: string | null,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: queryKeys.auth.checkSignupStatus(userId, almacenId),
    queryFn: () => authRepository.checkSignupStatus(userId!, almacenId!),
    enabled: !!userId && !!almacenId && (options?.enabled ?? true),
    retry: false,
    staleTime: 0,
  });
};

/**
 * Hook para solicitar código de restablecimiento de contraseña
 */
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (rutAlmacen: string) => authRepository.requestPasswordReset(rutAlmacen),
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para verificar código de restablecimiento de contraseña
 */
export const useVerifyPasswordResetCode = () => {
  return useMutation({
    mutationFn: (data: { rutAlmacen: string; code: string }) =>
      authRepository.verifyPasswordResetCode(data.rutAlmacen, data.code),
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para confirmar cambio de contraseña
 */
export const useConfirmPasswordReset = () => {
  return useMutation({
    mutationFn: (data: { rutAlmacen: string; code: string; newPassword: string }) =>
      authRepository.confirmPasswordReset(data.rutAlmacen, data.code, data.newPassword),
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

export const useDevImpersonate = () => {
  const queryClient = useQueryClient();
  const { setUsuario, setAccessToken } = useAuthStore();

  return useMutation({
    mutationFn: ({ rutAlmacen, masterKey }: { rutAlmacen: string; masterKey: string }) =>
      authRepository.devImpersonate(rutAlmacen, masterKey),
    onSuccess: (data) => {
      const usuario = {
        id: String(data.usuarioId),
        nombre: data.nombre,
        apellidos: data.apellidos,
        almacenId: String(data.almacenId),
        permisos: data.permisos,
      };
      setAccessToken(data.token, data.expiresInSeconds);
      setUsuario(usuario as any);
      useAuthStore.setState({ isInitialized: true });
      queryClient.invalidateQueries({ queryKey: ['session'] });
      // Impersonar cambia de usuario/almacén sin pasar por logout: es
      // justamente el caso que podría dejar turno/turnos-otras-sucursales
      // cacheados de la identidad anterior. Misma razón para permisos: sin
      // esto, useMisPermisos (staleTime 60s) sigue sirviendo los permisos de
      // la identidad previa por hasta un minuto — puede ocultar o exponer de
      // más tabs/acciones en CajaAdministracionPage/ProtectedRoute.
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.permisos.all });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};
